import config from '../../config';
import { getAuthHeaders, getAuthHeaderOnly, handleResponse, handleNoContentResponse } from './common';

export const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_ATTACHMENT_COUNT = 5;
export const ALLOWED_ATTACHMENT_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
    'video/mp4', 'video/quicktime', 'video/webm',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain', 'text/csv',
];

export function validateAttachments(files) {
    const errors = [];
    if (files.length > MAX_ATTACHMENT_COUNT) {
        errors.push(`You can attach up to ${MAX_ATTACHMENT_COUNT} files at a time.`);
    }
    for (const file of files) {
        if (file.size > MAX_ATTACHMENT_SIZE) {
            errors.push(`"${file.name}" exceeds the 25 MB limit.`);
        }
        if (file.type && !ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
            errors.push(`"${file.name}" has an unsupported file type.`);
        }
    }
    return errors;
}

const messageApi = {
    markMessagesAsRead: async (messageIds) => {
        const res = await fetch(`${config.api.url}/message/mark-read`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ messageIds })
        });
        return handleNoContentResponse(res);
    },

    fetchDirectConversations: async () => {
        const res = await fetch(`${config.api.url}/message/conversations`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    fetchUnreadCount: async () => {
        const res = await fetch(`${config.api.url}/message/unread-count`, {
            headers: getAuthHeaders()
        });
        const data = await handleResponse(res);
        return typeof data === 'number' ? data : data?.unreadCount ?? data?.count ?? 0;
    },

    fetchOrganizationMembers: async (organizationId) => {
        const url = `${config.api.url}/organizations/${organizationId}/members`;

        const res = await fetch(url, {
            headers: getAuthHeaders()
        });

        if (!res.ok) {
            const errorText = await res.text();
            console.error('API: Error response:', errorText);
            throw new Error(`Failed to fetch organization members: ${res.status} ${res.statusText}`);
        }

        const data = await res.json();
        return data;
    },

    fetchOrganizationConversations: async (organizationId) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/conversations`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    fetchOrganizationMessages: async (organizationId, conversationId = null, recipientId = null) => {
        let url = `${config.api.url}/organizations/${organizationId}/messages`;
        const params = new URLSearchParams();
        
        if (conversationId) {
            params.append('conversationId', conversationId);
        }
        if (recipientId) {
            params.append('recipientId', recipientId);
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const res = await fetch(url, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    sendOrganizationMessage: async (organizationId, messageData) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/messages`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(messageData)
        });
        return handleResponse(res);
    },

    sendMessageToActiveClients: async (organizationId, content) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/messages/active-clients`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ content })
        });
        return handleResponse(res);
    },

    markOrganizationMessagesAsRead: async (organizationId, messageIds) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/messages/read`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify({ messageIds: messageIds })
        });
        return handleNoContentResponse(res);
    },

    createOrganizationConversation: async (organizationId, conversationData) => {
        // conversationData may include: name, participantIds, type, description, isPublic
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/conversations`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(conversationData)
        });
        return handleResponse(res);
    },

    browseOrganizationChannels: async (organizationId) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/channels/browse`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    joinOrganizationChannel: async (organizationId, channelId) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/channels/${channelId}/join`, {
            method: "POST",
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    leaveOrganizationChannel: async (organizationId, channelId) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/channels/${channelId}/leave`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    fetchPracticeInboxConversations: async (params = {}) => {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                query.append(key, value);
            }
        });
        const qs = query.toString();
        const res = await fetch(`${config.api.url}/messages/conversations${qs ? `?${qs}` : ''}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    fetchPracticeInboxUnreadCount: async () => {
        const res = await fetch(`${config.api.url}/messages/unread-count`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    fetchPracticeInboxConversation: async (conversationReferenceId) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    fetchClientCareTeamConversation: async () => {
        const res = await fetch(`${config.api.url}/messages/client/conversation`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    sendClientCareTeamMessage: async (data) => {
        const res = await fetch(`${config.api.url}/messages/client/messages`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    createPracticeInboxConversation: async (data) => {
        const res = await fetch(`${config.api.url}/messages/conversations`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    sendPracticeInboxReply: async (conversationReferenceId, data) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/messages`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    updatePracticeInboxMessage: async (conversationReferenceId, messageReferenceId, body) => {
        const res = await fetch(
            `${config.api.url}/messages/conversations/${conversationReferenceId}/messages/${messageReferenceId}`,
            {
                method: "PATCH",
                headers: getAuthHeaders(),
                body: JSON.stringify({ body })
            }
        );
        return handleResponse(res);
    },

    deletePracticeInboxMessage: async (conversationReferenceId, messageReferenceId) => {
        const res = await fetch(
            `${config.api.url}/messages/conversations/${conversationReferenceId}/messages/${messageReferenceId}`,
            {
                method: "DELETE",
                headers: getAuthHeaders()
            }
        );
        return handleResponse(res);
    },

    createPracticeInboxInternalNote: async (conversationReferenceId, data) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/internal-notes`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    updatePracticeInboxAssignment: async (conversationReferenceId, assignedToUserReferenceId) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/assignment`, {
            method: "PATCH",
            headers: getAuthHeaders(),
            body: JSON.stringify({ assignedToUserReferenceId: assignedToUserReferenceId || null })
        });
        return handleResponse(res);
    },

    updatePracticeInboxStatus: async (conversationReferenceId, status) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/status`, {
            method: "PATCH",
            headers: getAuthHeaders(),
            body: JSON.stringify({ status })
        });
        return handleResponse(res);
    },

    markPracticeInboxRead: async (conversationReferenceId) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/read`, {
            method: "PATCH",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    markPracticeInboxUnread: async (conversationReferenceId) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/unread`, {
            method: "PATCH",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    generatePracticeInboxFaxTranscript: async (conversationReferenceId, data) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/fax-transcripts`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    fetchPracticeInboxFaxTranscript: async (conversationReferenceId, faxTranscriptReferenceId) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/fax-transcripts/${faxTranscriptReferenceId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    sendPracticeInboxFaxTranscript: async (conversationReferenceId, faxTranscriptReferenceId, data) => {
        const res = await fetch(`${config.api.url}/messages/conversations/${conversationReferenceId}/fax-transcripts/${faxTranscriptReferenceId}/send`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    fetchMessageTemplates: async ({ includeInactive = false } = {}) => {
        const params = new URLSearchParams();
        if (includeInactive) params.append('includeInactive', 'true');
        const qs = params.toString();
        const res = await fetch(`${config.api.url}/messages/templates${qs ? `?${qs}` : ''}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createMessageTemplate: async (data) => {
        const res = await fetch(`${config.api.url}/messages/templates`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    updateMessageTemplate: async (templateReferenceId, data) => {
        const res = await fetch(`${config.api.url}/messages/templates/${templateReferenceId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    deactivateMessageTemplate: async (templateReferenceId) => {
        const res = await fetch(`${config.api.url}/messages/templates/${templateReferenceId}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    updateOrganizationChannel: async (organizationId, channelId, data) => {
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/channels/${channelId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(res);
    },

    uploadAttachment: async (file) => {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${config.api.url}/message/attachments`, {
            method: 'POST',
            headers: getAuthHeaderOnly(),
            body: form,
        });
        return handleResponse(res);
    },

    uploadClientAttachment: async (file) => {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${config.api.url}/messages/client/attachments`, {
            method: 'POST',
            headers: getAuthHeaderOnly(),
            body: form,
        });
        return handleResponse(res);
    },

    uploadOrganizationAttachment: async (organizationId, file) => {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${config.api.url}/organizations/${organizationId}/messages/attachments`, {
            method: 'POST',
            headers: getAuthHeaderOnly(),
            body: form,
        });
        return handleResponse(res);
    },
};

export default messageApi;
